/**
 * API client for pots endpoints
 */

import { apiClient } from '../../../shared/api/axiosClient';
import { unwrap } from '@billda/api';
import type { PotDetailsV2Response } from '@billda/api';
import { api } from '../../../shared/api/billdaApi';
import type {
  BatchApprovePotsRequest,
  BatchApprovePotsResponse,
} from '../types/potApproval.types';
import type {
  BatchMarkPotsPaidRequest,
  BatchMarkPotsPaidResponse,
} from '../types/potPaid.types';
import type {
  MovePotToActivePeriodRequest,
  MovePotToActivePeriodResponse,
  MoveToPrimaryResponse,
} from '../types/potMove.types';
import type {
  RejectPotRequest,
  RejectPotResponse,
  AddRejectionReasonRequest,
  RejectionReasonOperationResponse,
} from '../types/potRejection.types';

export interface PotSummaryDto {
  id: string;
  ownerType: 'Solo' | 'Gang';
  ownerName: string;
  gangId: string | null;
  status: string;
  netAmount: number;
  allocationCount: number;
  nextDeadline: string | null;
  nextDeadlineType: 'BookIn' | 'Verify' | 'Submit' | null;
  isScheduledForAutoSubmit: boolean;
  isPrimary: boolean;
}

export interface GetPotsResponse {
  items: PotSummaryDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface GetPotsParams {
  potPeriodId: string;
  search?: string;
  page: number;
  pageSize: number;
}

export async function getPots(params: GetPotsParams): Promise<GetPotsResponse> {
  const queryParams = new URLSearchParams({
    potPeriodId: params.potPeriodId,
    page: params.page.toString(),
    pageSize: params.pageSize.toString(),
  });

  if (params.search) {
    queryParams.append('search', params.search);
  }

  const response = await apiClient.get<GetPotsResponse>(
    `/api/v1/pots?${queryParams.toString()}`
  );
  return response.data;
}

/**
 * Fetches detailed information for a specific pot using the V2 endpoint
 * @param potId - The unique identifier of the pot
 * @param signal - Optional AbortSignal to cancel the request
 * @returns Promise resolving to the raw V2 pot details response
 */
export async function getPotDetailsV2(
  potId: string,
  signal?: AbortSignal
): Promise<PotDetailsV2Response> {
  return unwrap(
    await api.legacy.GET('/api/v2/pots/{potId}', {
      params: { path: { potId } },
      signal,
    })
  );
}

/**
 * Batch approve pots in a single atomic transaction
 * @param request - The batch approval request containing pot period and pot IDs
 * @returns Promise resolving to approval summary with count and approved pot details
 */
export async function batchApprovePots(
  request: BatchApprovePotsRequest
): Promise<BatchApprovePotsResponse> {
  const response = await apiClient.post<BatchApprovePotsResponse>(
    '/api/v1/pots/batch-approve',
    request
  );
  return response.data;
}

/**
 * Batch mark pots as paid in a single atomic transaction
 * @param request - The batch mark paid request containing pot period and pot IDs
 * @returns Promise resolving to paid summary with count and paid pot details
 */
export async function batchMarkPotsPaid(
  request: BatchMarkPotsPaidRequest
): Promise<BatchMarkPotsPaidResponse> {
  const response = await apiClient.post<BatchMarkPotsPaidResponse>(
    '/api/v1/pots/batch-mark-paid',
    request
  );
  return response.data;
}

/**
 * Move a pot with MissedSubmitDeadline status to the current active period
 * @param request - The request containing the pot ID to move
 * @returns Promise resolving to move result with from/to period details
 */
export async function movePotToActivePeriod(
  request: MovePotToActivePeriodRequest
): Promise<MovePotToActivePeriodResponse> {
  const response = await apiClient.post<MovePotToActivePeriodResponse>(
    '/api/v1/pots/move/to-active-period',
    request
  );
  return response.data;
}

/**
 * Move a non-primary pot to primary (promote or merge)
 * @param potId - The unique identifier of the pot to move
 * @returns Promise resolving to the move result with action type and counts
 */
export async function movePotToPrimary(
  potId: string
): Promise<MoveToPrimaryResponse> {
  const response = await apiClient.post<MoveToPrimaryResponse>(
    `/api/v1/pots/${potId}/move-to-primary`
  );
  return response.data;
}

/**
 * Reject a pot with one or more reasons
 * @param potId - The unique identifier of the pot to reject
 * @param request - The rejection request containing reasons
 * @returns Promise resolving to rejection details
 */
export async function rejectPot(
  potId: string,
  request: RejectPotRequest
): Promise<RejectPotResponse> {
  const response = await apiClient.post<RejectPotResponse>(
    `/api/v1/pots/${potId}/reject`,
    request
  );
  return response.data;
}

/**
 * Add a rejection reason to a rejected pot
 * @param potId - The unique identifier of the pot
 * @param request - The request containing the reason
 * @returns Promise resolving to updated rejection reasons
 */
export async function addRejectionReason(
  potId: string,
  request: AddRejectionReasonRequest
): Promise<RejectionReasonOperationResponse> {
  const response = await apiClient.post<RejectionReasonOperationResponse>(
    `/api/v1/pots/${potId}/rejection-reasons`,
    request
  );
  return response.data;
}

/**
 * Mark a rejection reason as resolved
 * @param potId - The unique identifier of the pot
 * @param reasonId - The unique identifier of the rejection reason
 * @returns Promise resolving to updated rejection reasons
 */
export async function resolveRejectionReason(
  potId: string,
  reasonId: string
): Promise<RejectionReasonOperationResponse> {
  const response = await apiClient.post<RejectionReasonOperationResponse>(
    `/api/v1/pots/${potId}/rejection-reasons/${reasonId}/resolve`
  );
  return response.data;
}

/**
 * Remove (soft delete) a rejection reason
 * @param potId - The unique identifier of the pot
 * @param reasonId - The unique identifier of the rejection reason
 * @returns Promise resolving to updated rejection reasons
 */
export async function removeRejectionReason(
  potId: string,
  reasonId: string
): Promise<RejectionReasonOperationResponse> {
  const response = await apiClient.delete<RejectionReasonOperationResponse>(
    `/api/v1/pots/${potId}/rejection-reasons/${reasonId}`
  );
  return response.data;
}
